import type { ReportInterval } from '../types/reports';
import { periodEnd, periodsPerYear } from './reportPeriod';

/**
 * Jahreskonstante, Guard und Jahr/Periode -> `reportingPeriod`-Bereich fuer die Meldungenseite
 * (D-02 bis D-06). Gerechnet wird auf Jahres- und Monatszahlen und Template-Strings, **niemals**
 * ueber eine Umwandlung eines Datumsstrings zurueck in einen ISO-String -- ein negativer
 * UTC-Offset schoebe das Ergebnis sonst um einen Tag (siehe reportPeriod.ts). Die einzige
 * zulaessige `Date`-Nutzung in diesem Modul steckt in `currentReportYear()`.
 */

/**
 * Untergrenze der Jahresauswahl, als Literal -- NICHT aus Daten abgeleitet und NICHT vom Backend
 * geholt.
 *
 * (a) D-02: reine Anzeigelogik des Pickers, das Backend braucht sie nicht -- dort ist das Jahr
 *     nur ein Bereich auf `reportingPeriod`.
 * (b) D-03: der Wert wandert zum Produktivstart auf `2026`, weil 2026 das erste echte
 *     Berichtsjahr ist.
 * (c) der heutige Wert ist bewusst instabil gegenueber `app:demo:reports --purge` und darf
 *     deshalb NICHT an die Seed-Daten gekoppelt werden.
 */
export const REPORT_YEAR_RANGE_START = 2024;

/** Obergrenze nach D-04: eine Meldung fuer ein kuenftiges Jahr existiert nicht, auch nicht als Entwurf. */
export function currentReportYear(): number {
  return new Date().getFullYear();
}

/**
 * Liest den `year`-Suchparameter. `URLSearchParams.get()` liefert `null`, wenn der Schluessel
 * fehlt, und `''` bei `?year=`. Genau daran haengt D-06 -- ohne diese Unterscheidung reaktiviert
 * ein Reload die Vorbelegung, obwohl der Nutzer das Jahr bewusst geleert hat:
 *
 * - `raw === null` (Parameter fehlt komplett) -> Vorbelegung: das laufende Jahr
 * - `raw === ''` (bewusst geleert) -> `undefined`: alle Jahre (D-05)
 * - ein Wert ausserhalb `[REPORT_YEAR_RANGE_START, currentReportYear()]` oder nicht-numerisch
 *   faellt still auf die Vorbelegung zurueck (Guard nach `periodFilterModel.ts::selectedYear`,
 *   T-25-05) -- kein Wurf, keine unsinnige Serverabfrage.
 */
export function parseYearParam(raw: string | null): number | undefined {
  if (raw === null) {
    return currentReportYear();
  }

  if (raw === '') {
    return undefined;
  }

  const parsed = Number(raw);

  if (!Number.isInteger(parsed)) {
    return currentReportYear();
  }

  if (parsed < REPORT_YEAR_RANGE_START || parsed > currentReportYear()) {
    return currentReportYear();
  }

  return parsed;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * Rechnet Jahr und (optionale) Periode auf einen `reportingPeriod`-Bereich um -- die Feldnamen
 * entsprechen bewusst dem, was `ReportFilters` in Plan 03 bekommt, damit der Aufrufer nur noch
 * spreizen muss.
 *
 * Ohne Jahr: leeres Objekt (Pitfall 5 -- `getDraftCounts()` ruft `getAllReports({status:'draft'})`
 * ohne Jahr; es darf dabei kein halber Bereichsparameter entstehen).
 *
 * Mit Jahr und ohne verwertbare Periode (kein `interval`, `interval === 'yearly'`, `period`
 * fehlt/`'all'`, oder ein Periodenindex ausserhalb `1 … periodsPerYear(interval)`): der volle
 * Jahresbereich `${year}-01-01` … `${year}-12-31`.
 *
 * Mit Jahr und Periode: der Startmonat kommt aus dem Periodenindex (monatlich `index`,
 * quartalsweise `(index - 1) * 3 + 1`), die obere Grenze ueber das bestehende
 * `periodEnd(start, interval)` aus `reportPeriod.ts` -- keine zweite Datumsarithmetik und keine
 * eigene Schaltjahrregel.
 */
export function reportingPeriodRange(
  year: number | undefined,
  period: string | undefined,
  interval: ReportInterval | null
): { reportingPeriodAfter?: string; reportingPeriodBefore?: string } {
  if (year === undefined) {
    return {};
  }

  const yearRange = {
    reportingPeriodAfter: `${year}-01-01`,
    reportingPeriodBefore: `${year}-12-31`,
  };

  if (!interval || interval === 'yearly' || !period || period === 'all') {
    return yearRange;
  }

  const index = Number(period);

  if (!Number.isInteger(index) || index < 1 || index > periodsPerYear(interval)) {
    return yearRange;
  }

  const startMonth = interval === 'quarterly' ? (index - 1) * 3 + 1 : index;
  const start = `${year}-${pad2(startMonth)}-01`;

  return {
    reportingPeriodAfter: start,
    reportingPeriodBefore: periodEnd(start, interval),
  };
}
